import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { ArrowLeft, Plus, FileText, Download, CheckCircle, Clock, Lock, Save, Send, Sun } from 'lucide-react'
import { formatMyanmarDateTime } from '@/lib/dateUtils'

interface SiteSurveyInfo {
  id: number
  sr_number: string
  client_name: string
  contact_number: string
  address: string
}

interface SolarFormSummary {
  id: number
  site_survey_id: number
  date: string | null
  project_name: string
  contact_person: string
  status: string
  submitted_by: string | null
  submitted_at: string | null
  created_at: string
}

interface SolarFormData {
  id?: number
  site_survey_id: number
  date: string
  project_name: string
  contact_person: string
  contact_phone: string
  address: string
  form_data: Record<string, any>
  status?: string
  submitted_by?: string | null
  submitted_at?: string | null
}

const INVERTER_SIZES = ['3KW', '5KW', '6KW', '10KW', '15KW', 'Customize']
const BATTERY_CAPACITIES = ['100Ah', '10KW']
const PHASES = ['1 Phase', '3 Phase']
const BATTERY_SIZES = ['100Ah', '150Ah', '160Ah', '200Ah', '220Ah', '300Ah', '350Ah', 'Customize']
const MOUNTING_TYPES = ['Corrugated Iron', 'Roof Top Slab', 'Ground Mounted', 'Customize']
const SOLAR_POWERS = ['555W', '570W', '575W', '615W']
const SOLAR_CABLE_SIZES = ['4mm', '6mm', '10mm']
const SOLAR_DESIGNS = ['Series', 'Parallel']
const AC_SOURCES = ['1 Phase', '3 Phase']
const AC_CABLE_SIZES = ['4mm', '6mm']

function CheckboxGroup({ label, options, value, onChange, disabled }: {
  label: string
  options: string[]
  value: string
  onChange: (v: string) => void
  disabled?: boolean
}) {
  return (
    <div>
      <label className="text-xs text-slate-500 block mb-1">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map(o => (
          <label key={o} className={`flex items-center gap-1.5 text-sm cursor-pointer ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
            <input
              type="radio"
              name={label}
              value={o}
              checked={value === o}
              onChange={() => !disabled && onChange(o)}
              disabled={disabled}
              className="accent-red-800"
            />
            {o}
          </label>
        ))}
      </div>
    </div>
  )
}

function ToggleField({ label, fieldKey, formData, setFormData, disabled }: {
  label: string
  fieldKey: string
  formData: Record<string, any>
  setFormData: (d: Record<string, any>) => void
  disabled?: boolean
}) {
  const val = !!formData[fieldKey]
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-700">{label}</span>
      <div className="flex gap-2">
        {['Yes', 'No'].map(opt => (
          <button
            key={opt}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && setFormData({ ...formData, [fieldKey]: opt === 'Yes' })}
            className={`px-3 py-0.5 text-xs rounded-full font-medium border transition-colors ${
              (opt === 'Yes' ? val : !val)
                ? 'bg-red-800 text-white border-red-800'
                : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
            } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  )
}

function TextField({ label, fieldKey, formData, setFormData, disabled, placeholder }: {
  label: string
  fieldKey: string
  formData: Record<string, any>
  setFormData: (d: Record<string, any>) => void
  disabled?: boolean
  placeholder?: string
}) {
  return (
    <div>
      <label className="text-xs text-slate-500 block mb-1">{label}</label>
      <input
        type="text"
        value={formData[fieldKey] || ''}
        onChange={e => !disabled && setFormData({ ...formData, [fieldKey]: e.target.value })}
        disabled={disabled}
        placeholder={placeholder || ''}
        className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700 disabled:bg-slate-50 disabled:text-slate-500"
      />
    </div>
  )
}

export default function SolarSurveyForm({ survey, onClose }: { survey: SiteSurveyInfo; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [activeFormId, setActiveFormId] = useState<number | null>(null)
  const [view, setView] = useState<'list' | 'edit'>('list')
  const [form, setForm] = useState<SolarFormData>({
    site_survey_id: survey.id,
    date: new Date().toISOString().split('T')[0],
    project_name: survey.client_name || '',
    contact_person: survey.client_name || '',
    contact_phone: survey.contact_number || '',
    address: survey.address || '',
    form_data: {},
  })
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  const [downloadingId, setDownloadingId] = useState<number | null>(null)

  const { data: formList = [], isLoading: listLoading } = useQuery<SolarFormSummary[]>({
    queryKey: ['solar-forms-list', survey.id],
    queryFn: () => api.get(`/solar-survey/forms/${survey.id}`).then(r => r.data),
  })

  const { data: activeFormData, isLoading: formLoading } = useQuery({
    queryKey: ['solar-form', activeFormId],
    queryFn: () => api.get(`/solar-survey/form/${activeFormId}`).then(r => r.data),
    enabled: !!activeFormId,
  })

  useEffect(() => {
    if (activeFormData) {
      const fd = typeof activeFormData.form_data === 'string'
        ? JSON.parse(activeFormData.form_data)
        : (activeFormData.form_data || {})
      setForm({
        site_survey_id: survey.id,
        date: activeFormData.date || '',
        project_name: activeFormData.project_name || '',
        contact_person: activeFormData.contact_person || '',
        contact_phone: activeFormData.contact_phone || '',
        address: activeFormData.address || '',
        form_data: fd,
        status: activeFormData.status,
        submitted_by: activeFormData.submitted_by,
        submitted_at: activeFormData.submitted_at,
      })
    }
  }, [activeFormData, survey.id])

  const createMutation = useMutation({
    mutationFn: (data: any) => api.post('/solar-survey', data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['solar-forms-list', survey.id] })
      setActiveFormId(res.data.id)
      setSaved(true)
      setError('')
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'Failed to save'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: any) => api.put(`/solar-survey/form/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['solar-forms-list', survey.id] })
      queryClient.invalidateQueries({ queryKey: ['solar-form', activeFormId] })
      setSaved(true)
      setError('')
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'Failed to save'),
  })

  const submitMutation = useMutation({
    mutationFn: (id: number) => api.post(`/solar-survey/form/${id}/submit`),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['solar-forms-list', survey.id] })
      queryClient.invalidateQueries({ queryKey: ['solar-form', activeFormId] })
      setForm(prev => ({ ...prev, status: 'Submitted', submitted_by: res.data.submitted_by, submitted_at: res.data.submitted_at }))
      setError('')
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'Failed to submit'),
  })

  const handleNewForm = () => {
    setActiveFormId(null)
    setForm({
      site_survey_id: survey.id,
      date: new Date().toISOString().split('T')[0],
      project_name: survey.client_name || '',
      contact_person: survey.client_name || '',
      contact_phone: survey.contact_number || '',
      address: survey.address || '',
      form_data: {},
    })
    setSaved(false)
    setError('')
    setView('edit')
  }

  const handleOpenForm = (id: number) => {
    setActiveFormId(id)
    setSaved(false)
    setError('')
    setView('edit')
  }

  const handleSave = () => {
    const payload = {
      date: form.date,
      project_name: form.project_name,
      contact_person: form.contact_person,
      contact_phone: form.contact_phone,
      address: form.address,
      form_data: form.form_data,
    }
    if (activeFormId) {
      updateMutation.mutate({ id: activeFormId, ...payload })
    } else {
      createMutation.mutate({ site_survey_id: survey.id, ...payload })
    }
  }

  const handleSubmit = () => {
    if (!activeFormId) { setError('Please save the form first before submitting'); return }
    if (!confirm('Submit this form? Once submitted, it cannot be edited.')) return
    submitMutation.mutate(activeFormId)
  }

  const handleDownloadPDF = async (formId: number) => {
    setDownloadingId(formId)
    try {
      const resp = await api.get(`/solar-survey/form/${formId}/pdf`, { responseType: 'blob' })
      const url = URL.createObjectURL(resp.data)
      const a = document.createElement('a')
      a.href = url
      const contentDisposition = resp.headers['content-disposition'] || ''
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      a.download = match ? match[1] : `solar-survey-${formId}.pdf`
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      setError('Failed to download PDF')
    } finally {
      setDownloadingId(null)
    }
  }

  const fd = form.form_data
  const setFd = (key: string, val: any) => setForm(prev => ({ ...prev, form_data: { ...prev.form_data, [key]: val } }))
  const isSubmitted = form.status === 'Submitted'
  const isSaving = createMutation.isPending || updateMutation.isPending

  if (view === 'list') {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between sticky top-0 bg-white z-10 pb-3 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
              <ArrowLeft size={18} className="text-slate-600" />
            </button>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Solar Survey Forms</h2>
              <p className="text-xs text-slate-500">{survey.sr_number} — {survey.client_name}</p>
            </div>
          </div>
          <button onClick={handleNewForm}
            className="flex items-center gap-2 px-4 py-2 bg-red-800 text-white rounded-lg text-sm font-medium hover:bg-red-900">
            <Plus size={14} /> New Form
          </button>
        </div>

        {listLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-red-800 border-t-transparent rounded-full" />
          </div>
        ) : formList.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <Sun size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">No solar survey forms yet</p>
            <p className="text-xs mt-1">Click "New Form" to create the first one</p>
          </div>
        ) : (
          <div className="space-y-3">
            {formList.map(f => (
              <div key={f.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900 text-sm">{f.project_name || `Solar Form #${f.id}`}</span>
                    {f.status === 'Submitted' ? (
                      <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium flex items-center gap-1">
                        <Lock size={10} /> Submitted
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium flex items-center gap-1">
                        <Clock size={10} /> Draft
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{f.contact_person}{f.date ? ` · ${f.date}` : ''}</p>
                  {f.status === 'Submitted' && f.submitted_by && (
                    <p className="text-xs text-green-700 mt-1 flex items-center gap-1">
                      <CheckCircle size={10} />
                      Submitted by <span className="font-medium">{f.submitted_by}</span>
                      {f.submitted_at ? ` · ${formatMyanmarDateTime(f.submitted_at)}` : ''}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {f.status === 'Submitted' && (
                    <button onClick={() => handleDownloadPDF(f.id)} disabled={downloadingId === f.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50">
                      <Download size={12} /> {downloadingId === f.id ? 'Downloading...' : 'PDF'}
                    </button>
                  )}
                  <button onClick={() => handleOpenForm(f.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-800 text-white rounded-lg hover:bg-red-900">
                    <FileText size={12} /> Open
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  if (formLoading && activeFormId) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-4 border-red-800 border-t-transparent rounded-full" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="sticky top-0 bg-white z-10 pb-2 border-b border-slate-200">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={() => setView('list')} className="p-1.5 hover:bg-slate-100 rounded-lg shrink-0">
              <ArrowLeft size={18} className="text-slate-600" />
            </button>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-slate-900 truncate">Solar Survey Form</h2>
              <p className="text-xs text-slate-500 truncate">{survey.sr_number} · {form.project_name || 'New Form'}</p>
            </div>
          </div>
          {!isSubmitted && (
            <div className="flex items-center gap-1.5 shrink-0">
              <button onClick={handleSave} disabled={isSaving}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-700 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50">
                <Save size={12} /> {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={handleSubmit} disabled={submitMutation.isPending || !activeFormId}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                <Send size={12} /> Submit
              </button>
            </div>
          )}
          {isSubmitted && activeFormId && (
            <button onClick={() => handleDownloadPDF(activeFormId)} disabled={downloadingId === activeFormId}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50">
              <Download size={12} /> {downloadingId === activeFormId ? '...' : 'PDF'}
            </button>
          )}
        </div>
        {saved && <p className="text-xs text-green-700 mt-1 flex items-center gap-1"><CheckCircle size={10} /> Saved successfully</p>}
        {isSubmitted && (
          <div className="mt-1 flex items-center gap-1.5 text-xs text-green-700 bg-green-50 rounded-lg px-3 py-1.5">
            <Lock size={10} /> Submitted by {form.submitted_by}
            {form.submitted_at ? ` · ${formatMyanmarDateTime(form.submitted_at)}` : ''}
          </div>
        )}
        {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      </div>

      <div className="space-y-4 pb-4">
        {/* Header Info */}
        <div className="bg-slate-50 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-semibold text-slate-700">Survey Information</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Project Name</label>
              <input value={form.project_name} onChange={e => setForm({ ...form, project_name: e.target.value })} disabled={isSubmitted}
                className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700 disabled:bg-slate-100 disabled:text-slate-500" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Date</label>
              <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} disabled={isSubmitted}
                className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700 disabled:bg-slate-100 disabled:text-slate-500" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Contact Person</label>
              <input value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} disabled={isSubmitted}
                className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700 disabled:bg-slate-100 disabled:text-slate-500" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Phone</label>
              <input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} disabled={isSubmitted}
                className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700 disabled:bg-slate-100 disabled:text-slate-500" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Address</label>
            <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} disabled={isSubmitted}
              className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700 disabled:bg-slate-100 disabled:text-slate-500" />
          </div>
        </div>

        {/* Inverter */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Sun size={14} className="text-yellow-500" /> Inverter</h3>
          <CheckboxGroup label="Inverter Size" options={INVERTER_SIZES} value={fd.inverter_size || ''} onChange={v => setFd('inverter_size', v)} disabled={isSubmitted} />
          {fd.inverter_size === 'Customize' && (
            <TextField label="Custom Inverter Size" fieldKey="inverter_size_custom" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
          )}
          <CheckboxGroup label="Battery Capacity" options={BATTERY_CAPACITIES} value={fd.battery_capacity || ''} onChange={v => setFd('battery_capacity', v)} disabled={isSubmitted} />
          <CheckboxGroup label="Phase" options={PHASES} value={fd.phase || ''} onChange={v => setFd('phase', v)} disabled={isSubmitted} />
        </div>

        {/* Battery */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-semibold text-slate-700">Battery</h3>
          <CheckboxGroup label="Battery Size" options={BATTERY_SIZES} value={fd.battery_size || ''} onChange={v => setFd('battery_size', v)} disabled={isSubmitted} />
          {fd.battery_size === 'Customize' && (
            <TextField label="Custom Battery Size" fieldKey="battery_size_custom" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
          )}
        </div>

        {/* Solar Mounting & Power */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-semibold text-slate-700">Solar Mounting & Power</h3>
          <CheckboxGroup label="Mounting Type" options={MOUNTING_TYPES} value={fd.mounting_type || ''} onChange={v => setFd('mounting_type', v)} disabled={isSubmitted} />
          {fd.mounting_type === 'Customize' && (
            <TextField label="Custom Mounting Type" fieldKey="mounting_type_custom" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
          )}
          <CheckboxGroup label="Solar Panel Wattage" options={SOLAR_POWERS} value={fd.solar_power || ''} onChange={v => setFd('solar_power', v)} disabled={isSubmitted} />
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Number of Strings" fieldKey="string_count" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} placeholder="e.g. 2" />
            <CheckboxGroup label="Solar Cable Size" options={SOLAR_CABLE_SIZES} value={fd.solar_cable_size || ''} onChange={v => setFd('solar_cable_size', v)} disabled={isSubmitted} />
            <TextField label="Solar Cable Length (m)" fieldKey="solar_cable_length" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} placeholder="e.g. 50" />
            <TextField label="Solar Location" fieldKey="solar_location" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
          </div>
        </div>

        {/* Solar Design & Structure */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-semibold text-slate-700">Solar Design & Structure</h3>
          <CheckboxGroup label="Solar Design" options={SOLAR_DESIGNS} value={fd.solar_design || ''} onChange={v => setFd('solar_design', v)} disabled={isSubmitted} />
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Solar Frame" fieldKey="solar_frame" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
            <TextField label="Surrounding Obstacle" fieldKey="surrounding_obstacle" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
            <TextField label="Roof Facing Navigation" fieldKey="roof_facing" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} placeholder="e.g. South" />
            <TextField label="Inverter Location" fieldKey="inverter_location" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
            <TextField label="Battery Location" fieldKey="battery_location" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
            <TextField label="DC Cable Length" fieldKey="dc_cable_length" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} placeholder="e.g. 10m" />
            <TextField label="Earth Location" fieldKey="earth_location" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
            <TextField label="Building Design" fieldKey="building_design" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
            <TextField label="Roof Type" fieldKey="roof_type" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
            <TextField label="Number of Floors" fieldKey="num_floors" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} placeholder="e.g. 3" />
          </div>
        </div>

        {/* AC Source */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-semibold text-slate-700">AC Source</h3>
          <CheckboxGroup label="AC Source Phase" options={AC_SOURCES} value={fd.ac_source || ''} onChange={v => setFd('ac_source', v)} disabled={isSubmitted} />
          <div className="grid grid-cols-2 gap-3">
            <CheckboxGroup label="AC Cable Size" options={AC_CABLE_SIZES} value={fd.ac_cable_size || ''} onChange={v => setFd('ac_cable_size', v)} disabled={isSubmitted} />
            <TextField label="AC Cable Length (m)" fieldKey="ac_cable_length" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} placeholder="e.g. 15" />
          </div>
        </div>

        {/* Yes/No Items */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Checklist</h3>
          <div className="divide-y divide-slate-100">
            {[
              ['Line Separate', 'line_separate'],
              ['Hacking', 'hacking'],
              ['Trunking', 'trunking'],
              ['Piping', 'piping'],
              ['ATS', 'ats'],
              ['Shadow', 'shadow'],
              ['RCCB Breaker', 'rccb_breaker'],
              ['Earth', 'earth'],
            ].map(([label, key]) => (
              <ToggleField key={key} label={label} fieldKey={key} formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
            ))}
          </div>
        </div>

        {/* Load Estimation */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-semibold text-slate-700">Load Estimation</h3>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Load Estimate" fieldKey="load_estimate" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} placeholder="kW" />
            <TextField label="Day Load" fieldKey="day_load" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} placeholder="kW" />
            <TextField label="Night Load" fieldKey="night_load" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} placeholder="kW" />
            <TextField label="Hidden Load" fieldKey="hidden_load" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} placeholder="kW" />
            <TextField label="Water Cooler" fieldKey="water_cooler" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
            <TextField label="Water Heater" fieldKey="water_heater" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
          </div>
        </div>

        {/* Electrical Sizing */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-semibold text-slate-700">Electrical Sizing</h3>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Wire Size" fieldKey="wire_size" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
            <TextField label="Main Breaker Size" fieldKey="main_breaker_size" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
            <TextField label="Transformer Size" fieldKey="transformer_size" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
            <TextField label="Generator Size" fieldKey="generator_size" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
            <TextField label="Total Load at Maximum" fieldKey="total_load_max" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} placeholder="kW" />
            <TextField label="Total Load for Useable Appliance" fieldKey="total_load_useable" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} placeholder="kW" />
            <TextField label="Back Up Time" fieldKey="backup_time" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} placeholder="hours" />
          </div>
        </div>

        {/* Resources */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-semibold text-slate-700">Resources & Logistics</h3>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Man Power" fieldKey="man_power" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} placeholder="Number" />
            <TextField label="Car" fieldKey="car" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} placeholder="Number" />
            <TextField label="Generator" fieldKey="generator" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} placeholder="Number" />
            <TextField label="Ladder" fieldKey="ladder" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} placeholder="Number" />
            <TextField label="HSE Request" fieldKey="hse_request" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
            <TextField label="Number of Days" fieldKey="num_days" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} placeholder="Days" />
            <TextField label="Welding" fieldKey="welding" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
            <TextField label="Scaffolding" fieldKey="scaffolding" formData={fd} setFormData={d => setForm({ ...form, form_data: d })} disabled={isSubmitted} />
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <label className="text-sm font-semibold text-slate-700 block mb-2">Notes</label>
          <textarea
            value={fd.note || ''}
            onChange={e => !isSubmitted && setFd('note', e.target.value)}
            disabled={isSubmitted}
            rows={4}
            placeholder="Any additional notes..."
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700 disabled:bg-slate-50 disabled:text-slate-500"
          />
        </div>

        {!isSubmitted && (
          <div className="flex gap-3 pt-2">
            <button onClick={handleSave} disabled={isSaving}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50">
              <Save size={14} /> {isSaving ? 'Saving...' : 'Save Draft'}
            </button>
            <button onClick={handleSubmit} disabled={submitMutation.isPending || !activeFormId}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
              <Send size={14} /> Submit
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
